// Live updates: the app's screens come from our server, so a deploy reaches
// phones without a Play release (api/src/modules/app/updates.js). On launch
// and when the app comes back to the front, ask what this app should run; a
// newer bundle downloads in the background and switches in the next time the
// app is put away or reopened, never mid-job. Offline: nothing changes. A
// bundle that fails to start is rolled back by the plugin (notifyAppReady).
// If the server says this app is too old, a screen asks them to update it
// from Play.
import { useSyncExternalStore } from 'react';
import { App as CapApp } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { config, isNative } from '../config.js';

export const WEB_BUILD = import.meta.env.VITE_WEB_BUILD;
export const PLAY_URL = 'market://details?id=uk.co.nanoutilities.app';
const EVERY = 15 * 60_000;

let state = { native: null, updateFromPlay: false, ready: null, webReady: false };
const listeners = new Set();
const set = (patch) => { state = { ...state, ...patch }; listeners.forEach((fn) => fn()); };
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export function useAppUpdate() {
  return useSyncExternalStore(subscribe, () => state);
}

async function nativeInfo() {
  if (state.native) return state.native;
  const i = await CapApp.getInfo();
  set({ native: { version: i.version, build: i.build } });
  return state.native;
}

let lastCheck = 0;
let checking = false;

export async function checkForUpdate() {
  if (!isNative || checking || Date.now() - lastCheck < EVERY || !navigator.onLine) return;
  checking = true;
  lastCheck = Date.now();
  try {
    const { build } = await nativeInfo();
    const res = await fetch(`${config.apiBase}/api/app/update?code=${encodeURIComponent(build)}`, { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
    if (!res.ok) return;
    const { bundle, updateFromPlay } = await res.json();
    set({ updateFromPlay: Boolean(updateFromPlay) });
    if (!bundle || bundle.version === WEB_BUILD || bundle.version === state.ready) return;
    // Already downloaded (an earlier check that didn't get to queue it)?
    const { bundles } = await CapacitorUpdater.list();
    const have = bundles.find((b) => b.version === bundle.version && b.status !== 'error');
    const got = have || await CapacitorUpdater.download({ url: new URL(bundle.url, config.apiBase).href, version: bundle.version, checksum: bundle.checksum });
    await CapacitorUpdater.next({ id: got.id });
    set({ ready: bundle.version });
  } catch {
    // No signal or a bad download: try again next time. Nothing changes.
  } finally {
    checking = false;
  }
}

// "Update now" (side menu, Support): check straight away and, if there's a
// newer build, switch to it at once (the screens restart). Taps not yet sent
// stay in the outbox, so nothing is lost. Returns 'latest', 'offline', 'play'
// (needs a Play update), 'error', or 'updating' (the app is restarting).
export async function updateNow() {
  if (!navigator.onLine) return 'offline';
  try {
    if (!isNative) {
      const res = await fetch(`${config.apiBase}/api/app/update?code=0`, { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
      const { version } = await res.json();
      if (!version || version === WEB_BUILD) return 'latest';
      window.location.reload();
      return 'updating';
    }
    const { build } = await nativeInfo();
    const res = await fetch(`${config.apiBase}/api/app/update?code=${encodeURIComponent(build)}`, { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
    if (!res.ok) return 'error';
    const { bundle, updateFromPlay } = await res.json();
    if (updateFromPlay) { set({ updateFromPlay: true }); return 'play'; }
    if (!bundle || bundle.version === WEB_BUILD) return 'latest';
    const { bundles } = await CapacitorUpdater.list();
    const have = bundles.find((b) => b.version === bundle.version && b.status !== 'error');
    const got = have || await CapacitorUpdater.download({ url: new URL(bundle.url, config.apiBase).href, version: bundle.version, checksum: bundle.checksum });
    await CapacitorUpdater.set({ id: got.id }); // switches and restarts the screens now
    return 'updating';
  } catch {
    return navigator.onLine ? 'error' : 'offline';
  }
}

export const UPDATE_TEXT = {
  checking: 'Checking for an update…',
  updating: 'Updating…',
  latest: 'You have the latest version.',
  offline: 'No signal. Try again when you have signal.',
  play: 'This version is too old: update the app from Play.',
  error: 'Couldn’t update. Try again in a minute.',
};

// The app's version (from Play) and which build of the screens it runs.
export function versionText(native) {
  return isNative && native ? `${native.version} · ${WEB_BUILD}` : import.meta.env.VITE_APP_VERSION;
}

// In a browser: a tab left open keeps the screens it loaded, so check for a
// newer build every few minutes. When there is one, a bar offers Reload, and
// it reloads by itself the next time the tab comes back to the front (taps
// not yet sent are kept on the device, so nothing is lost).
const WEB_EVERY = 5 * 60_000;
async function checkWeb() {
  if (state.webReady || !navigator.onLine || !WEB_BUILD || WEB_BUILD === 'dev') return;
  try {
    const res = await fetch(`${config.apiBase}/api/app/update?code=0`, { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
    if (!res.ok) return;
    const { version } = await res.json();
    if (version && version !== WEB_BUILD) set({ webReady: true });
  } catch { /* try again later */ }
}
export function reloadForUpdate() {
  window.location.reload();
}
function startWebUpdates() {
  checkWeb();
  setInterval(checkWeb, WEB_EVERY);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (state.webReady) reloadForUpdate();
    else checkWeb();
  });
}

export function startLiveUpdates() {
  if (!isNative) { startWebUpdates(); return; }
  // This bundle started: keep it (otherwise the plugin rolls back).
  CapacitorUpdater.notifyAppReady().catch(() => {});
  nativeInfo().catch(() => {});
  checkForUpdate();
  CapApp.addListener('appStateChange', ({ isActive }) => { if (isActive) checkForUpdate(); });
  window.addEventListener('online', () => checkForUpdate());
}
